import { flash } from "@xola/ui-kit";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Virtuoso } from "react-virtuoso";
import { Guide, Permission } from "types";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { sortBy } from "../../../utils/locale";
import { PromptModal } from "../../Common/PromptModal";
import { ErrorBoundary } from "../../ErrorBoundary";
import { Loader } from "../../Loader";
import { GuideItem } from "./GuideItem";

interface GuideManagementProps {
    height: number;
}

export const GuideManagement = ({ height }: GuideManagementProps) => {
    const seller = useSeller();
    const user = useUser();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const showAllGuides = isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW);
    const [isDelete, setDelete] = useState(false);
    const [guideId, setGuideId] = useState<ID | null>(null);

    const {
        data: guides,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<Guide>(["sellers", seller.id, "guides"]);

    const filteredGuides = showAllGuides ? guides : guides.filter((guide: Guide) => guide.user.id === user.id);
    const sortedGuides = sortBy(filteredGuides, "name");

    const { mutateAsync: deleteGuide } = useMutation(async (guideId: ID) => {
        return api.delete(`/sellers/${seller.id}/guides/${guideId}`);
    });

    const onDelete = async (guideId: string) => {
        setGuideId(guideId);
        toggleDeleteModal();
    };

    const toggleDeleteModal = () => {
        setDelete(!isDelete);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteGuide(guideId!);
            invalidate("guides");
            invalidate("delegates"); // List of delegates
        } catch (error: any) {
            flash.show({ text: error?.response?.data?.field?.guide?.message ?? "", color: "danger" });
        } finally {
            toggleDeleteModal();
        }
    };

    return (
        <div className="flex h-full flex-col border-l border-gray-lighter">
            <Virtuoso
                style={{ height }}
                data={sortedGuides}
                endReached={async () => {
                    if (hasNextPage) {
                        await fetchNextPage();
                    }
                }}
                itemContent={(index, guide) => {
                    return (
                        <ErrorBoundary fallbackMessage={`Error rendering guide ${guide.id}`}>
                            <GuideItem key={guide.id} guide={guide} onDelete={onDelete} />
                        </ErrorBoundary>
                    );
                }}
            />
            {isFetchingNextPage ? <Loader /> : <div className="h-16" />}

            <PromptModal
                title="Delete Guide?"
                isOpen={isDelete}
                onConfirm={handleDeleteConfirm}
                onClose={toggleDeleteModal}
            >
                Are you sure you want to delete this guide?
            </PromptModal>
        </div>
    );
};
